import React, { useState } from 'react'
import { useSubmitPaymentProof } from '@/hooks/usePaymentProofs'
import { FileUpload } from '@/components/common/FileUpload'

interface Props {
  enrollmentId: string
  courseName: string
  amount: number
  targetBankConfig?: { bankName: string; accountNumber: string; accountHolderName: string } | null
  onSuccess: () => void
  onCancel: () => void
}

export const PaymentProofUploadForm: React.FC<Props> = ({
  enrollmentId,
  courseName,
  amount,
  targetBankConfig = null,
  onSuccess,
  onCancel,
}) => {
  const [bankName, setBankName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [accountHolderName, setAccountHolderName] = useState('')
  const [proofFileUrl, setProofFileUrl] = useState('')
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const submitMutation = useSubmitPaymentProof()

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!bankName.trim()) newErrors.bankName = 'Nama bank wajib diisi'
    if (!accountNumber.trim()) newErrors.accountNumber = 'Nomor rekening wajib diisi'
    if (!accountHolderName.trim()) newErrors.accountHolderName = 'Nama pemilik rekening wajib diisi'
    if (!proofFileUrl.trim()) newErrors.proofFileUrl = 'Link bukti transfer wajib diisi'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    submitMutation.mutate(
      {
        enrollmentId,
        input: {
          amount_paid: amount,
          bank_name: bankName,
          account_number: accountNumber,
          account_holder_name: accountHolderName,
          proof_file_url: proofFileUrl,
          notes: notes || undefined,
        },
      },
      {
        onSuccess: () => {
          onSuccess()
        },
        onError: (err: Error) => {
          setErrors({ submit: err.message })
        },
      }
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-surface-card rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-white/10">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Upload Bukti Pembayaran</h2>
          <p className="text-sm text-gray-500 dark:text-text-muted mb-1">
            Course: <span className="font-medium text-gray-700 dark:text-white">{courseName}</span>
          </p>
          <p className="text-sm text-gray-500 dark:text-text-muted mb-4">
            Nominal: <span className="font-bold text-primary">Rp {amount.toLocaleString('id-ID')}</span>
          </p>

          {targetBankConfig && (
            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4 mb-4 space-y-2">
              <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider">
                Rekening Tujuan
              </p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-text-muted">Bank</span>
                <span className="font-bold dark:text-white">{targetBankConfig.bankName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-text-muted">No. Rekening</span>
                <span className="font-bold dark:text-white font-mono">{targetBankConfig.accountNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-text-muted">Atas Nama</span>
                <span className="font-bold dark:text-white">{targetBankConfig.accountHolderName}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="bank-name" className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1.5">
                Nama Bank Pengirim *
              </label>
              <input
                id="bank-name"
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="Contoh: BCA, Mandiri, BRI"
              />
              {errors.bankName && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.bankName}</p>}
            </div>

            <div>
              <label htmlFor="account-number" className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1.5">
                Nomor Rekening Pengirim *
              </label>
              <input
                id="account-number"
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="Masukkan nomor rekening"
              />
              {errors.accountNumber && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.accountNumber}</p>}
            </div>

            <div>
              <label htmlFor="account-holder" className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1.5">
                Atas Nama *
              </label>
              <input
                id="account-holder"
                type="text"
                value={accountHolderName}
                onChange={(e) => setAccountHolderName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="Nama pemilik rekening"
              />
              {errors.accountHolderName && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.accountHolderName}</p>}
            </div>

            <div>
              <FileUpload
                type="image"
                accept="image/*,.pdf"
                onUpload={(url: string) => setProofFileUrl(url)}
                label="Upload Bukti Transfer"
              />
              {errors.proofFileUrl && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.proofFileUrl}</p>}
            </div>

            <div>
              <label htmlFor="notes" className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1.5">
                Catatan (Opsional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="Catatan tambahan untuk admin"
              />
            </div>

            {errors.submit && (
              <div className="bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-sm p-3 rounded-lg border border-red-200 dark:border-red-500/20">
                {errors.submit}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-4 py-3 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={submitMutation.isPending}
                className="flex-1 px-4 py-3 bg-primary text-white rounded-lg text-sm font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-primary/20"
              >
                {submitMutation.isPending ? 'Mengirim...' : 'Kirim Bukti'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
